import { createSelector } from '@reduxjs/toolkit';
import { PRIORITY_NOTIFY_MANUAL_TOGGLE } from '../../constants/optimizelyExperiments';
import { getTranslation } from '../../providers/TranslationProvider';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';
import TicketIcon from '../../svg/icons/ic_ticket_20x20.svg';

const CONSTANTS = {
  EVENT_SERVICE_TYPE_IDS: [6, 7, 8],
  CANCEL_SUCCESS_MESSAGE: 'Notify cancelled!',
  SET_NOTIFY_SUCCESS_MESSAGE: (start, end) => `Notification from ${start} to ${end} created`,
  SET_NOTIFY: 'set_notify',
  TABLES_AVAILABLE: 'tables_available',
  SET_NOTIFY_SUCCESS: 'set_notify_success',
  BUTTON_ID_CHANGE: 'notify-button-change',
  BUTTON_ID_CONFIRM: 'notify-button-confirm',
  BUTTON_ID_CLOSE: 'notify-button-close',
  BUTTON_ID_SUCCESS: 'notify-button-success',
  BUTTON_ID_CANCEL: 'notify-button-cancel',
  BUTTON_ID_TABLES_AVAILABLE: 'notify-button-tables_available',
  POST_NOTIFY: 'post_notify',
};
const SERVICE_TYPES = {
  5: {
    name: 'breakfast',
    startTime: '06:00:00',
    endTime: '12:00:00',
  },
  1: {
    name: 'brunch',
    startTime: '09:00:00',
    endTime: '16:00:00',
  },
  3: {
    name: 'lunch',
    startTime: '10:00:00',
    endTime: '16:30:00',
  },
  2: {
    name: 'dinner',
    startTime: '17:00:00',
    endTime: '24:00:00',
  },
  6: {
    name: 'event 1',
    startTime: '00:00:00',
    endTime: '24:00:00',
  },
  7: {
    name: 'event 2',
    startTime: '00:00:00',
    endTime: '24:00:00',
  },
  8: {
    name: 'event 3',
    startTime: '00:00:00',
    endTime: '24:00:00',
  },
};
const NOOP = () => { };

const EMPTY_EXPERIMENT = {};
const selectPriorityNotifyExperiment = (state) => (
  state?.experiments?.[PRIORITY_NOTIFY_MANUAL_TOGGLE] || EMPTY_EXPERIMENT
);

const getPriorityNotifyExperiment = createSelector(
  [selectPriorityNotifyExperiment],
  (experiment) => {
    const {
      variation_key: variationKey,
      enabled,
      variables,
    } = experiment;

    return {
      experimentKey: variables?.notify_experiment_name,
      variationKey,
      variables,
      isActive: Boolean(enabled && variationKey && variationKey !== 'off'),
    };
  }
);

// Derives the priority-notify toggle visibility, auto-enable, and API flag value.
function getPriorityNotifyState({
  isGda,
  isPriorityExperimentActive,
  experimentVariables,
  isExistingNotify,
  isPriorityNotify,
}) {
  const shouldShowPriorityToggle = isGda
    && isPriorityExperimentActive
    && experimentVariables?.behavior?.show_priority_notify_toggle
    && !isExistingNotify;
  const requiresMemberTimeSelection = isPriorityExperimentActive
    && experimentVariables?.behavior?.require_member_time_selection;
  const isPriorityNotifyAutoEnabled = !experimentVariables?.behavior?.show_priority_notify_toggle;
  const priorityNotifyValue = isExistingNotify
    ? isPriorityNotify
    : !shouldShowPriorityToggle || isPriorityNotify;
  const priorityNotifyEnabled = isPriorityExperimentActive && isGda
    ? priorityNotifyValue
    : undefined;

  return {
    shouldShowPriorityToggle,
    requiresMemberTimeSelection,
    isPriorityNotifyAutoEnabled,
    priorityNotifyValue,
    priorityNotifyEnabled,
  };
}

// Derives the header, body, and CTA copy, preferring experiment copy when active.
function getNotifyCopy({
  isGda,
  isPriorityExperimentActive,
  isExistingNotify,
  isPriorityNotify,
  experimentVariables,
}) {
  const experimentNotifyCopy = isExistingNotify
    ? experimentVariables?.edit_notify
    : experimentVariables?.create_notify;

  const defaultNotifyHeader = getTranslation({
    translationKey: isGda ? 'set_priority_notify' : 'set_notify',
    fallback: isGda ? 'Set Priority Notify' : 'Set Notify',
  });
  const defaultNotifyBody = getTranslation({
    translationKey: 'notify_dialog_body',
    fallback: 'If a table opens up, you will receive an email or push notification (if enabled).',
  });
  const defaultNotifyChangeOrConfirm = getTranslation({
    translationKey: isExistingNotify ? 'change_time' : 'button_confirm',
    fallback: isExistingNotify ? 'Change Time' : 'Confirm',
  });

  const notifyHeader = (isPriorityExperimentActive
    && (isExistingNotify && isPriorityNotify
      ? experimentNotifyCopy?.title_priority
      : experimentNotifyCopy?.title))
    || defaultNotifyHeader;
  const notifyBody = (isPriorityExperimentActive && experimentNotifyCopy?.instruction)
    || defaultNotifyBody;
  const notifyChangeOrConfirm = (isPriorityExperimentActive && experimentNotifyCopy?.cta_confirm)
    || defaultNotifyChangeOrConfirm;

  return { notifyHeader, notifyBody, notifyChangeOrConfirm };
}

// Derives the venue/event display details (label, name, icon, quantity copy).
function getNotifyDetails({
  activeNotifications,
  venueName,
  eventName,
  selectedQuantity,
}) {
  const detailsServiceTypeId = activeNotifications
    ? parseInt(Object.keys(activeNotifications)[0], 10)
    : null;
  const isEvent = CONSTANTS.EVENT_SERVICE_TYPE_IDS.includes(detailsServiceTypeId);
  const notifyQuantity = getTranslation({
    translationKey: isEvent ? 'number_tickets' : 'number_guests',
    translationValues: { [isEvent ? 'seats' : 'number']: `${selectedQuantity}` },
    fallback: `${selectedQuantity} ${isEvent ? 'Tickets' : 'Guests'}`,
  });

  return {
    isEvent,
    detailsLabel: isEvent ? venueName : '',
    detailsName: isEvent ? eventName : venueName,
    iconComponent: isEvent ? TicketIcon : PeopleIcon,
    iconName: isEvent ? 'ticket' : 'people',
    notifyQuantity,
  };
}

// Derives the displayed start/end time values, dropdown options, and confirm-disabled state.
function getNotifyTimeDisplay({
  requiresMemberTimeSelection,
  isRequiredTimeCreate,
  notifyStart,
  notifyEnd,
  selectedNotifyStart,
  selectedNotifyEnd,
  notifyTimeOptions,
}) {
  const displayNotifyStart = isRequiredTimeCreate ? selectedNotifyStart : notifyStart;
  const displayNotifyEnd = isRequiredTimeCreate ? selectedNotifyEnd : notifyEnd;
  const timeOptions = requiresMemberTimeSelection
    ? [{ label: '', value: '' }, ...notifyTimeOptions]
    : notifyTimeOptions;
  const isConfirmDisabled = requiresMemberTimeSelection && (!displayNotifyStart || !displayNotifyEnd);

  return {
    displayNotifyStart, displayNotifyEnd, timeOptions, isConfirmDisabled,
  };
}

// Derives the GDA/priority-notify-aware button variant shared by pre- and post-state CTAs.
function getPriorityNotifyButtonVariant({
  isGda,
  isPriorityExperimentActive,
  isPriorityNotify,
  isPriorityNotifyAutoEnabled,
}) {
  const defaultVariant = isGda ? 'rga' : 'primary';
  const priorityVariant = (isPriorityNotify || isPriorityNotifyAutoEnabled) ? 'rga' : 'primary';

  return isPriorityExperimentActive ? priorityVariant : defaultVariant;
}

// Derives the confirm/change button's variant and test id.
function getChangeOrConfirmCta({
  isGda,
  isPriorityExperimentActive,
  isPriorityNotify,
  isPriorityNotifyAutoEnabled,
  isExistingNotify,
}) {
  const changeOrConfirmVariant = getPriorityNotifyButtonVariant({
    isGda,
    isPriorityExperimentActive,
    isPriorityNotify,
    isPriorityNotifyAutoEnabled,
  });
  const changeOrConfirmTestId = isExistingNotify
    ? CONSTANTS.BUTTON_ID_CHANGE
    : CONSTANTS.BUTTON_ID_CONFIRM;

  return { changeOrConfirmVariant, changeOrConfirmTestId };
}

// Static translation labels shared by the notify pre-state view.
function getNotifyStaticLabels() {
  return {
    startEndLabel: getTranslation({ translationKey: 'notify_start_end_label', fallback: 'Preferred Start and End Time' }),
    toLabel: getTranslation({ translationKey: 'notify_to', fallback: 'to' }),
    preferredStart: getTranslation({ translationKey: 'notify_preferred_start', fallback: 'Preferred start time' }),
    preferredEnd: getTranslation({ translationKey: 'notify_preferred_end', fallback: 'Preferred end time' }),
    notifyRemove: getTranslation({ translationKey: 'remove', fallback: 'Remove' }),
    calendarIcon: getTranslation({ translationKey: 'calendar_icon', fallback: 'Calendar icon' }),
    partySizeIcon: getTranslation({ translationKey: 'party_size_icon', fallback: 'Party size icon' }),
  };
}

/**
 * This allows for opening the modal contextually where the use is scrolled on the widget
 * Ultimately the value comes from outside the widget because
 * the scrolling mechanism is outside the iframe in
 * modalContainer
 * So the embed code broadcasts that value to the widget
 * using postMessage
 */
function getInset(scrollTop) {
  if (scrollTop) {
    return {
      inset: `${scrollTop}px 0 auto`,
    };
  }
  return undefined;
}

function getAuthModalStyles(breakpoints, scrollTop) {
  const desktopStyles = {
    borderRadius: '10px',
    bottom: 'auto',
    top: '10%',
    ...getInset(scrollTop),
  };

  const mobileStyles = {
    top: '15vh',
    left: 0,
    right: 0,
    bottom: 0,
    margin: '0 auto',
  };

  const responsiveStyles = breakpoints.xs && !breakpoints.sm ? mobileStyles : desktopStyles;

  return {
    content: {
      backgroundColor: '#fff',
      border: 'none',
      boxShadow: '0 10px 50px 0 rgba(0, 0, 0, 0.1)',
      margin: '0 auto',
      maxWidth: '600px',
      padding: '0',
      ...responsiveStyles,
    },
  };
}

export {
  CONSTANTS,
  SERVICE_TYPES,
  NOOP,
  getPriorityNotifyExperiment,
  getPriorityNotifyState,
  getNotifyCopy,
  getNotifyDetails,
  getNotifyTimeDisplay,
  getPriorityNotifyButtonVariant,
  getChangeOrConfirmCta,
  getNotifyStaticLabels,
  getAuthModalStyles,
};
